/**
 * The IANA zone list, built from the engine's own `Intl.supportedValuesOf` —
 * no zone-data dependency. A curated label map names the zones a user is
 * likely to reach for ("US Central · America/Chicago"); everything else shows
 * its bare IANA id. No country inference.
 */

export interface ZoneOption {
  id: string;
  region: string;
  label: string;
  search: string;
}

export const ZONE_LABELS: Record<string, string> = {
  'Asia/Kolkata': 'India',
  'America/New_York': 'US Eastern',
  'America/Chicago': 'US Central',
  'America/Denver': 'US Mountain',
  'America/Phoenix': 'US Arizona',
  'America/Los_Angeles': 'US Pacific',
  'America/Anchorage': 'US Alaska',
  'Pacific/Honolulu': 'US Hawaii',
  'America/Toronto': 'Canada Eastern',
  'America/Vancouver': 'Canada Pacific',
  'America/Mexico_City': 'Mexico City',
  'America/Sao_Paulo': 'Brazil (São Paulo)',
  'Europe/London': 'UK',
  'Europe/Dublin': 'Ireland',
  'Europe/Paris': 'France',
  'Europe/Berlin': 'Central Europe',
  'Europe/Madrid': 'Spain',
  'Europe/Rome': 'Italy',
  'Europe/Amsterdam': 'Netherlands',
  'Europe/Stockholm': 'Sweden',
  'Europe/Istanbul': 'Türkiye',
  'Europe/Moscow': 'Moscow',
  'Asia/Dubai': 'Gulf (Dubai)',
  'Asia/Riyadh': 'Saudi Arabia',
  'Asia/Karachi': 'Pakistan',
  'Asia/Dhaka': 'Bangladesh',
  'Asia/Colombo': 'Sri Lanka',
  'Asia/Kathmandu': 'Nepal',
  'Asia/Bangkok': 'Thailand',
  'Asia/Jakarta': 'Indonesia (Jakarta)',
  'Asia/Singapore': 'Singapore',
  'Asia/Kuala_Lumpur': 'Malaysia',
  'Asia/Manila': 'Philippines',
  'Asia/Hong_Kong': 'Hong Kong',
  'Asia/Shanghai': 'China',
  'Asia/Tokyo': 'Japan',
  'Asia/Seoul': 'Korea',
  'Australia/Sydney': 'Sydney',
  'Australia/Melbourne': 'Melbourne',
  'Australia/Perth': 'Perth',
  'Pacific/Auckland': 'New Zealand',
  'Africa/Lagos': 'Nigeria',
  'Africa/Nairobi': 'Kenya',
  'Africa/Johannesburg': 'South Africa',
  'Africa/Cairo': 'Egypt',
  UTC: 'Coordinated Universal Time',
};

/**
 * tzdata "backward" links that older ICU builds still enumerate in place of the
 * modern primary name — Node 24 lists `Asia/Calcutta` where Chrome lists
 * `Asia/Kolkata`. Without this rewrite the zone a user picks would depend on
 * the engine that rendered the list, and India (the product default) would be
 * missing entirely from the Node-rendered one.
 */
const ZONE_ALIASES: Record<string, string> = {
  'Africa/Asmera': 'Africa/Asmara',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Etc/UTC': 'UTC',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

const UTC_ID = 'UTC';
/** Region bucket for the slashless ids (`UTC`). */
const OFFSET_REGION = 'Etc';

export function isValidTimeZone(id: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: id });
    return true;
  } catch {
    return false;
  }
}

function engineZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    const list = intl.supportedValuesOf?.('timeZone');
    if (list && list.length > 0) return list;
  } catch {
    /* engine without supportedValuesOf — fall through to the curated set */
  }
  return Object.keys(ZONE_LABELS);
}

/**
 * Only follow an alias when this engine actually knows the modern name.
 *
 * Exported because `listTimeZones` only ever yields canonical ids, so a stored
 * value written under a legacy name — Go's `time.LoadLocation` accepts
 * `Asia/Calcutta` just as happily as `Asia/Kolkata` — would match no row in a
 * picker built from that list. Callers seeding a picker from persisted data
 * run the value through this first.
 */
export function canonicalId(id: string): string {
  const alias = ZONE_ALIASES[id];
  return alias && isValidTimeZone(alias) ? alias : id;
}

let cachedIds: readonly string[] | null = null;

function zoneIds(): readonly string[] {
  if (cachedIds) return cachedIds;
  const ids = new Set(engineZones().map(canonicalId));
  // ECMA-402 requires UTC in the enumeration; Node's ICU omits it.
  ids.add(UTC_ID);
  // Backstop: a curated zone must never be missing because the engine listed it
  // under a name this module doesn't alias.
  for (const id of Object.keys(ZONE_LABELS)) {
    if (isValidTimeZone(id)) ids.add(id);
  }
  cachedIds = [...ids];
  return cachedIds;
}

/** 'GMT+5:30' / 'GMT-5' for the offset in force at `at`; '' if it can't be formatted. */
export function zoneGmtOffset(id: string, at: Date = new Date()): string {
  const parts = /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(zoneTimeZoneNamePart(id, at, 'shortOffset'));
  if (!parts) return '';
  const [, sign, hours, minutes] = parts;
  if (!sign || (Number(hours) === 0 && Number(minutes ?? 0) === 0)) return 'GMT';
  // ICU builds disagree on the zero offset ("GMT" vs "GMT+0"); the rest is normalised the same way.
  return `GMT${sign}${Number(hours)}${Number(minutes ?? 0) ? `:${minutes}` : ''}`;
}

/** 'India (GMT+5:30) · Asia/Kolkata': the curated name, the offset in force now, then the id. */
export function zoneLabel(id: string, at: Date = new Date()): string {
  const alias = ZONE_ALIASES[id];
  const name = ZONE_LABELS[id] ?? (alias ? ZONE_LABELS[alias] : undefined);
  const canonical = canonicalId(id);
  const offset = zoneGmtOffset(canonical, at);
  if (!name) return offset ? `${canonical} (${offset})` : canonical;
  return `${name}${offset ? ` (${offset})` : ''} · ${canonical}`;
}

export function listTimeZones(): ZoneOption[] {
  return zoneIds()
    .map((id) => {
      const slash = id.indexOf('/');
      const region = slash > 0 ? id.slice(0, slash) : OFFSET_REGION;
      const label = zoneLabel(id);
      return { id, region, label, search: `${label} ${id.replace(/_/g, ' ')}`.toLowerCase() };
    })
    .sort((a, b) => a.region.localeCompare(b.region) || a.label.localeCompare(b.label));
}

const NAME_FORMATS = new Map<string, Intl.DateTimeFormat>();

function zoneTimeZoneNamePart(id: string, at: Date, style: 'short' | 'long' | 'shortOffset'): string {
  try {
    const key = `${style}|${id}`;
    let format = NAME_FORMATS.get(key);
    if (!format) {
      format = new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: style });
      NAME_FORMATS.set(key, format);
    }
    const part = format.formatToParts(at).find((p) => p.type === 'timeZoneName');
    return part?.value ?? id;
  } catch {
    // Unknown zone, or an Invalid Date — formatToParts throws on both.
    return id;
  }
}

/** 'CDT' / 'GMT+5:30' for the offset in force at `at`; the id if it can't be formatted. */
export function zoneAbbrev(id: string, at: Date = new Date()): string {
  return zoneTimeZoneNamePart(id, at, 'short');
}

/** 'India Standard Time' / 'Gulf Standard Time' for the offset in force at `at` —
 *  the full English name Figma uses for the schedule note and the review
 *  summary's "Sends" line; the id if it can't be formatted. */
export function zoneLongName(id: string, at: Date = new Date()): string {
  return zoneTimeZoneNamePart(id, at, 'long');
}

/** Reads an HTML date/time pair as a wall-clock value in `zone`. */
export function zonedDateTimeToISO(date: string, time: string, zone: string): string {
  const [year = 0, month = 0, day = 0] = date.split('-').map(Number);
  const [hour = 0, minute = 0] = time.split(':').map(Number);
  const utc = Date.UTC(year, month - 1, day, hour, minute);
  const offsetAt = (instant: number) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(instant));
    const values = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
    return Date.UTC(Number(values.year ?? 0), Number(values.month ?? 1) - 1, Number(values.day ?? 1), Number(values.hour ?? 0), Number(values.minute ?? 0), Number(values.second ?? 0)) - instant;
  };
  const first = utc - offsetAt(utc);
  return new Date(utc - offsetAt(first)).toISOString();
}
